import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  Switch,
} from '@ibsys/kit';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IntegrationResponse } from '@/entities/integrations';
import { useUpdateIntegrationMockMode } from '@/entities/integrations';
import { useHasPermission } from '@/entities/profile';
import { toastError } from '@/shared/lib/utils/toast';

type Props = {
  integration: IntegrationResponse;
};

export const MockModeSection: FC<Props> = ({ integration }) => {
  const { t } = useTranslation(['integrations']);
  const canAdmin = useHasPermission('ADMIN_INTEGRATION_INFO');
  const { mutateAsync, isPending } = useUpdateIntegrationMockMode();

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const mockMode = Boolean(integration.mockMode);

  const updateMode = async (enabled: boolean) => {
    try {
      await mutateAsync({ code: integration.code, payload: { enabled } });
    } catch {
      toastError(t('integrations:general.saveError'));
    }
  };

  const handleToggle = (next: boolean) => {
    if (mockMode && !next) {
      // Переключаем в боевой режим — спрашиваем подтверждение
      setIsConfirmOpen(true);
      return;
    }
    void updateMode(next);
  };

  const handleConfirmProduction = async () => {
    setIsConfirmOpen(false);
    await updateMode(false);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-3 text-xl">
          {t('integrations:mockMode.title')}
          <Badge variant={mockMode ? 'primary' : 'destructive'}>
            {mockMode ? t('integrations:mockMode.mock') : t('integrations:mockMode.production')}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Switch
          value={mockMode}
          onChange={handleToggle}
          disabled={!canAdmin || isPending}
          label={t('integrations:mockMode.toggleLabel')}
        />
      </CardContent>

      <DialogRoot open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('integrations:mockMode.switchToProductionTitle')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{t('integrations:mockMode.switchToProductionDescription')}</p>
          <DialogFooter>
            <div className="flex gap-3 justify-end w-full">
              <Button variant="ghost" onClick={() => setIsConfirmOpen(false)}>
                {t('integrations:mockMode.cancel')}
              </Button>
              <Button onClick={handleConfirmProduction} disabled={isPending}>
                {t('integrations:mockMode.confirm')}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </Card>
  );
};
